import { GameState, TeamInLeague, Player, League, TransferHistoryRecord, NewsItem } from "../domain/entities";
import { MAX_SQUAD_SIZE, isPlayerInTransferCooldown, calculateReleasePenalty } from "../domain/constants";
import { addNewsItem } from './GameService';
// FIX: Update import to use the newly added getPlayerValue function in constants.
import { getPlayerValue } from "../domain/constants";

const BASE_TRANSFER_LEVY_PERCENT = 5;
const LEVY_INCREASE_PER_TRANSFER = 2.5;
const MAX_LEVY_PERCENT = 25;

const calculatePlayerTransferScore = (player: (Player & { askingPrice: number }), teamAverageSkill: number, teamBalance: number): number => {
    let score = 0;

    const skillImprovement = player.skill - teamAverageSkill;
    score += skillImprovement * 2;

    if (player.age < 22) score += 15;
    else if (player.age < 25) score += 10;
    else if (player.age > 30) score -= (player.age - 30) * 2;

    const valueRatio = player.askingPrice / teamBalance;
    if (valueRatio > 0.5) score -= 10;
    else if (valueRatio < 0.2) score += 5;

    score += player.form;

    return score;
};

/**
 * Minimum squad composition requirements
 */
const POSITION_MINIMUMS = {
    GK: 2,   // Goalkeepers
    DEF: 6,  // Defenders
    MID: 6,  // Midfielders
    FWD: 4   // Forwards
};

const getTotalMinimumSquadSize = (): number => {
    return Object.values(POSITION_MINIMUMS).reduce((sum, count) => sum + count, 0);
};

/**
 * Checks team position composition against minimum requirements
 */
const getPositionDeficits = (team: TeamInLeague): { hasDeficits: boolean, deficits: { [pos: string]: number } } => {
    const positionCounts: { [key: string]: number } = { GK: 0, DEF: 0, MID: 0, FWD: 0 };

    team.squad.forEach(player => {
        if (positionCounts[player.position] !== undefined) {
            positionCounts[player.position]++;
        }
    });

    const deficits: { [pos: string]: number } = {};
    let hasDeficits = false;

    Object.entries(POSITION_MINIMUMS).forEach(([pos, minimum]) => {
        const current = positionCounts[pos] || 0;
        if (current < minimum) {
            deficits[pos] = minimum - current;
            hasDeficits = true;
        }
    });

    return { hasDeficits, deficits };
};


export const updateAITransferLists = (gameState: GameState): GameState => {
    const newState: GameState = JSON.parse(JSON.stringify(gameState));
    (newState as any).gameStarted = true;
    const newListedIds = new Set<number>(newState.playersForSale);

    Object.values(newState.leagues).forEach((league: League) => {
        league.teams.filter(t => t.name !== newState.playerTeamName).forEach(team => {
            if (team.squad.length < 18) return;

            const potentialListings: Player[] = [];

            team.squad.forEach(player => {
                let listScore = 0;
                if (player.form <= -2) listScore += 3;
                if (player.lastSeasonChange < 0) listScore += 1;
                
                if (team.finances.balance < -500000) {
                     listScore += (getPlayerValue(player, league.name, newState.gameType, newState.config) / 2000000);
                }
                if (team.squad.length > MAX_SQUAD_SIZE - 2) {
                    listScore += (50 - player.skill) / 10;
                }
                
                if (player.age > 31) {
                    listScore += (player.age - 31);
                }

                listScore += Math.random();

                if (listScore > 3.5) {
                    potentialListings.push(player);
                }
            });

            const sortedSquad = [...team.squad].sort((a, b) => getPlayerValue(b, league.name, newState.gameType, newState.config) - getPlayerValue(a, league.name, newState.gameType, newState.config));
            const topPlayerIds = sortedSquad.slice(0, 3).map(p => p.id);

            potentialListings.forEach(p => {
                if (team.finances.balance > 0 && topPlayerIds.includes(p.id)) return;
                newListedIds.add(p.id);
            });
        });
    });
    
    newState.playersForSale = Array.from(newListedIds);
    return newState;
};


export const performAITransfers = (gameState: GameState): { newState: GameState, transferCount: number } => {
    const newState: GameState = JSON.parse(JSON.stringify(gameState));
    (newState as any).gameStarted = true;
    let transferCount = 0;
    let promotionCount = 0; // Track youth promotions for batching news
    let availableForSale: (Player & { originalTeamName: string; askingPrice: number; originalLeagueName: string; })[] = [];

    const populateSaleList = () => {
        availableForSale = [];
        Object.values(newState.leagues).forEach(league => {
            league.teams.forEach(team => {
                team.squad.forEach(player => {
                    // Only transfer-listed players can be bought directly during off-season
                    // Non-listed players must go through the bidding system (AIBiddingService)
                    if (newState.playersForSale.includes(player.id)) {
                        availableForSale.push({
                            ...player,
                            originalTeamName: team.name,
                            originalLeagueName: league.name,
                            askingPrice: getPlayerValue(player, league.name, newState.gameType, newState.config)
                        });
                    }
                });
            });
        });
    };

    populateSaleList();

    const MIN_AI_SQUAD_SIZE = 16;
    const MIN_CASH_RESERVE = 250000;
    const allTeamsMap = new Map<string, TeamInLeague>(
        Object.values(newState.leagues).flatMap((l: League) => l.teams).map(t => [t.name, t])
    );
    const allAiTeams = Array.from(allTeamsMap.values()).filter(t => t.name !== newState.playerTeamName);

    allAiTeams.forEach(team => {
        let attempts = 0;
        const targetSquadSize = getTotalMinimumSquadSize(); // 18 players minimum for position requirements

        while ((team.squad.length < Math.max(MIN_AI_SQUAD_SIZE, targetSquadSize) || getPositionDeficits(team).hasDeficits) && attempts < 12) {
            attempts++;
            let actionTaken = false;

            if (team.squad.length < MAX_SQUAD_SIZE && team.youthAcademy.squad.length > 0) {
                const squadAverage = team.squad.reduce((sum, p) => sum + p.skill, 0) / (team.squad.length || 1);
                const bestYouth = [...team.youthAcademy.squad].sort((a, b) => b.skill - a.skill)[0];

                if (bestYouth && bestYouth.skill > squadAverage - 12) {
                    const playerIndex = team.youthAcademy.squad.findIndex(p => p.id === bestYouth.id);
                    const [promotedPlayer] = team.youthAcademy.squad.splice(playerIndex, 1);
                    promotedPlayer.isPromotedYouth = true;
                    team.squad.push(promotedPlayer);
                    promotionCount++; // Track for batching instead of individual news items
                    actionTaken = true;
                }
            }

            if (actionTaken) continue;

            // Check position deficits
            const { hasDeficits, deficits } = getPositionDeficits(team);

            // Allow lower cash reserve if team has position deficits
            const effectiveMinCash = hasDeficits ? 75000 : MIN_CASH_RESERVE;
            if (team.finances.balance < effectiveMinCash) {
                // If still missing positions and have any cash, try one more time
                if (!hasDeficits) {
                    break;
                }
            }

            const playersNeeded = Math.max(MIN_AI_SQUAD_SIZE, targetSquadSize) - team.squad.length;
            const budget = Math.max(0, team.finances.balance - effectiveMinCash) * 0.8;
            const budgetPerPlayer = Math.max(50000, budget / (playersNeeded || 1));

            const positionSkills: { [key: string]: { skills: number[], count: number } } = { GK: { skills: [], count: 0 }, DEF: { skills: [], count: 0 }, MID: { skills: [], count: 0 }, FWD: { skills: [], count: 0 } };
            team.squad.forEach(p => {
                positionSkills[p.position].skills.push(p.skill);
                positionSkills[p.position].count++;
            });

            // Determine which position to fill, prioritizing those below minimums
            let targetPosition: string | null = null;
            let isBelowMinimum = false;

            // First, find positions below their minimum requirements
            const positionPriority = [
                { pos: 'GK', priority: 0 },
                { pos: 'DEF', priority: 1 },
                { pos: 'MID', priority: 2 },
                { pos: 'FWD', priority: 3 }
            ];

            for (const { pos, priority } of positionPriority) {
                const current = positionSkills[pos].count;
                const minimum = POSITION_MINIMUMS[pos as keyof typeof POSITION_MINIMUMS];
                if (current < minimum) {
                    targetPosition = pos;
                    isBelowMinimum = true;
                    break;
                }
            }

            // If all positions meet minimums, target the weakest by average skill
            if (!targetPosition) {
                const avgSkills = Object.entries(positionSkills).map(([pos, data]) => ({
                    pos,
                    avg: data.count > 0 ? data.skills.reduce((a, b) => a + b, 0) / data.count : 0,
                    count: data.count
                })).sort((a, b) => a.count - b.count || a.avg - b.avg);
                targetPosition = avgSkills[0].pos;
            }

            const squadAvgSkill = team.squad.reduce((sum, p) => sum + p.skill, 0) / (team.squad.length || 1);

            // More lenient requirements for positions below minimum
            const skillThreshold = isBelowMinimum ? squadAvgSkill - 20 : squadAvgSkill - 10;
            const budgetMultiplier = isBelowMinimum ? 2.0 : 1.3;

            const potentialBuys = availableForSale
                .filter(p => p.originalTeamName !== team.name)
                .filter(p => p.position === targetPosition)
                .filter(p => p.askingPrice < budgetPerPlayer * budgetMultiplier && p.askingPrice < team.finances.balance - effectiveMinCash)
                .filter(p => p.skill > skillThreshold)
                .map(p => ({ player: p, score: calculatePlayerTransferScore(p, squadAvgSkill, team.finances.balance) }))
                .sort((a, b) => b.score - a.score);

            if (potentialBuys.length > 0) {
                const target = potentialBuys[0].player;
                const sellerTeam = allTeamsMap.get(target.originalTeamName);

                // Allow AI teams to trade with each other during squad building
                // AI teams can trade freely as long as they keep at least 10 players
                // Player's team requires more protection (16 minimum)
                const minSellerSquadSize = sellerTeam?.name === newState.playerTeamName ? MIN_AI_SQUAD_SIZE : 10;
                if (sellerTeam && sellerTeam.squad.length > minSellerSquadSize) {
                    const transferFee = target.askingPrice;
                    team.finances.balance -= transferFee;
                    sellerTeam.finances.balance += transferFee;

                    // Record financial transactions for both teams
                    team.finances.history.push({
                        season: newState.season,
                        week: newState.currentWeek,
                        event: `Purchased ${target.name} from ${sellerTeam.name}`,
                        amount: -transferFee
                    });
                    sellerTeam.finances.history.push({
                        season: newState.season,
                        week: newState.currentWeek,
                        event: `Sold ${target.name} to ${team.name}`,
                        amount: transferFee
                    });

                    const playerIndex = sellerTeam.squad.findIndex(p => p.id === target.id);
                    if (playerIndex > -1) {
                        const [player] = sellerTeam.squad.splice(playerIndex, 1);
                        team.squad.push(player);

                        const historyRecord: TransferHistoryRecord = {
                            season: newState.season + 1,
                            week: 0,
                            playerName: player.name,
                            playerSkill: player.skill,
                            fromTeam: sellerTeam.name,
                            toTeam: team.name,
                            fee: transferFee,
                            playerId: player.id,
                            playerAge: player.age,
                            playerPosition: player.position,
                            playerPotential: player.potential,
                        };
                        newState.transferHistory.push(historyRecord);

                        transferCount++;

                        availableForSale = availableForSale.filter(p => p.id !== target.id);
                        actionTaken = true;
                    }
                }
            }

            if (!actionTaken) break;
        }
    });

    allAiTeams.forEach(team => {
        // Skip Phase 2 if squad is full, balance is low, or team has position deficits
        const { hasDeficits } = getPositionDeficits(team);
        if (team.squad.length >= MAX_SQUAD_SIZE || team.finances.balance < 1000000 || hasDeficits) return;

        const squadAvgSkill = team.squad.length > 0 ? team.squad.reduce((sum, p) => sum + p.skill, 0) / team.squad.length : 0;

        const positionSkills: { [key: string]: number[] } = { GK: [], DEF: [], MID: [], FWD: [] };
        team.squad.forEach(p => positionSkills[p.position].push(p.skill));
        const avgSkills = Object.entries(positionSkills).map(([pos, skills]) => ({
            pos, avg: skills.length > 0 ? skills.reduce((a, b) => a + b, 0) / skills.length : 0
        })).sort((a, b) => a.avg - b.avg);
        const weakestPosition = avgSkills[0].pos;

        const potentialBuys = availableForSale
            .filter(p => p.originalTeamName !== team.name)
            .filter(p => p.position === weakestPosition)
            .filter(p => p.askingPrice < team.finances.balance * 0.7)
            .filter(p => p.skill > squadAvgSkill)
            .map(p => ({ player: p, score: calculatePlayerTransferScore(p, squadAvgSkill, team.finances.balance) }))
            .sort((a, b) => b.score - a.score);

        if (potentialBuys.length > 0 && potentialBuys[0].score > 15) {
            const target = potentialBuys[0].player;
            const sellerTeam = allTeamsMap.get(target.originalTeamName);

            // Allow AI teams to trade with each other during squad building
            // AI teams can trade freely as long as they keep at least 10 players
            // Player's team requires more protection (16 minimum)
            const minSellerSquadSize = sellerTeam?.name === newState.playerTeamName ? MIN_AI_SQUAD_SIZE : 10;
            if (sellerTeam && sellerTeam.squad.length > minSellerSquadSize) {
                const transferFee = target.askingPrice;
                if (team.finances.balance > transferFee) {
                    team.finances.balance -= transferFee;
                    sellerTeam.finances.balance += transferFee;

                    // Record financial transactions for both teams
                    team.finances.history.push({
                        season: newState.season,
                        week: newState.currentWeek,
                        event: `Purchased ${target.name} from ${sellerTeam.name}`,
                        amount: -transferFee
                    });
                    sellerTeam.finances.history.push({
                        season: newState.season,
                        week: newState.currentWeek,
                        event: `Sold ${target.name} to ${team.name}`,
                        amount: transferFee
                    });

                    const playerIndex = sellerTeam.squad.findIndex(p => p.id === target.id);
                    if (playerIndex > -1) {
                        const [player] = sellerTeam.squad.splice(playerIndex, 1);
                        team.squad.push(player);

                        const historyRecord: TransferHistoryRecord = {
                            season: newState.season + 1,
                            week: 0,
                            playerName: player.name,
                            playerSkill: player.skill,
                            fromTeam: sellerTeam.name,
                            toTeam: team.name,
                            fee: transferFee,
                            playerId: player.id,
                            playerAge: player.age,
                            playerPosition: player.position,
                            playerPotential: player.potential,
                        };
                        newState.transferHistory.push(historyRecord);

                        transferCount++;

                        availableForSale = availableForSale.filter(p => p.id !== target.id);
                    }
                }
            }
        }
    });

    // Add batched news item for youth promotions
    if (promotionCount > 0) {
        if (promotionCount === 1) {
            addNewsItem(
                newState,
                'Youth Promotion',
                '1 young player has been promoted from their academy to the senior squad.',
                'youth',
                undefined,
                { type: 'youth', title: 'Youth Academy' }
            );
        } else {
            addNewsItem(
                newState,
                'Youth Promotions',
                `${promotionCount} young players have been promoted from their academies to senior squads across the league.`,
                'youth',
                undefined,
                { type: 'youth', title: 'Youth Academy' }
            );
        }
    }

    return { newState, transferCount };
};

export const simulateAIInSeasonTransfers = (gameState: GameState): { newState: GameState, transferCount: number } => {
    const newState: GameState = JSON.parse(JSON.stringify(gameState));
    (newState as any).gameStarted = true;
    let transferCount = 0;

    let availableForSale: (Player & { originalTeamName: string; askingPrice: number; originalLeagueName: string; })[] = [];
    const listedPlayerIds = new Set<number>(newState.playersForSale);

    Object.values(newState.leagues).forEach(league => {
        league.teams.forEach(team => {
            team.squad.forEach(player => {
                if (listedPlayerIds.has(player.id)) {
                    availableForSale.push({
                        ...player,
                        originalTeamName: team.name,
                        originalLeagueName: league.name,
                        askingPrice: getPlayerValue(player, league.name, newState.gameType, newState.config)
                    });
                }
            });
        });
    });

    if (availableForSale.length === 0) {
        return { newState, transferCount };
    }

    const allTeamsMap = new Map<string, TeamInLeague>(
        Object.values(newState.leagues).flatMap((l: League) => l.teams).map(t => [t.name, t])
    );
    const allAiTeams = Array.from(allTeamsMap.values()).filter(t => t.name !== newState.playerTeamName);

    for (const team of allAiTeams) {
        // 40% chance per team per week to look for transfers (increased from 20%)
        const isActivelyLooking = Math.random() < 0.4;
        if (!isActivelyLooking) {
            continue;
        }

        let motivation: 'buy_upgrade' | 'buy_replace' | 'sell_financial' | 'sell_squad' | 'none' = 'none';

        if (team.finances.balance < -500000) {
            motivation = 'sell_financial';
        } else if (team.squad.some(p => p.injury && p.injury.duration > 8 && p.skill > team.baseSkill - 5)) {
            motivation = 'buy_replace';
        } else if (team.squad.length < MAX_SQUAD_SIZE && team.finances.balance > 2000000) {
            motivation = 'buy_upgrade';
        } else if (team.squad.length >= MAX_SQUAD_SIZE - 1) {
            motivation = 'sell_squad';
        }

        if (motivation === 'none') {
            continue;
        }

        if (motivation === 'buy_upgrade' || motivation === 'buy_replace') {
            const positionSkills: { [key: string]: number[] } = { GK: [], DEF: [], MID: [], FWD: [] };
            team.squad.forEach(p => positionSkills[p.position].push(p.skill));
            const avgSkills = Object.entries(positionSkills).map(([pos, skills]) => ({
                pos,
                avg: skills.length > 0 ? skills.reduce((a, b) => a + b, 0) / skills.length : 0
            })).sort((a, b) => a.avg - b.avg);
            
            const weakestPosition = avgSkills[0].pos;
            const weakestPosAvgSkill = avgSkills[0].avg || team.baseSkill - 10;

            const potentialBuys = availableForSale
                .filter(p => p.originalTeamName !== team.name)
                .filter(p => p.position === weakestPosition)
                .filter(p => p.askingPrice < team.finances.balance * 0.4)
                .filter(p => p.skill > weakestPosAvgSkill)
                .map(p => ({ player: p, score: calculatePlayerTransferScore(p, weakestPosAvgSkill, team.finances.balance) }))
                .sort((a, b) => b.score - a.score);

            if (potentialBuys.length > 0 && potentialBuys[0].score > 10) {
                const target = potentialBuys[0].player;
                const sellerTeam = allTeamsMap.get(target.originalTeamName);
                
                if (!sellerTeam) continue;

                const transferFee = target.askingPrice;
                
                const transfersMadeByBuyer = newState.transferHistory.filter(t => t.season === newState.season && (t.fromTeam === team.name || t.toTeam === team.name)).length;
                const levyPercent = Math.min(MAX_LEVY_PERCENT, BASE_TRANSFER_LEVY_PERCENT + (transfersMadeByBuyer * LEVY_INCREASE_PER_TRANSFER));
                const levyAmount = Math.round(transferFee * (levyPercent / 100));
                const sellerProceeds = transferFee - levyAmount;

                team.finances.balance -= transferFee;
                sellerTeam.finances.balance += sellerProceeds;

                // Record financial transactions for both teams
                team.finances.history.push({
                    season: newState.season,
                    week: newState.currentWeek,
                    event: `Purchased ${target.name} from ${sellerTeam.name}`,
                    amount: -transferFee
                });
                sellerTeam.finances.history.push({
                    season: newState.season,
                    week: newState.currentWeek,
                    event: `Sold ${target.name} to ${team.name}`,
                    amount: sellerProceeds
                });

                const playerIndex = sellerTeam.squad.findIndex(p => p.id === target.id);
                if (playerIndex === -1) continue;

                const [player] = sellerTeam.squad.splice(playerIndex, 1);
                team.squad.push(player);

                const historyRecord: TransferHistoryRecord = {
                    season: newState.season,
                    week: newState.currentWeek,
                    playerName: player.name,
                    playerSkill: player.skill,
                    fromTeam: sellerTeam.name,
                    toTeam: team.name,
                    fee: transferFee,
                    playerId: player.id,
                    playerAge: player.age,
                    playerPosition: player.position,
                    playerPotential: player.potential,
                };
                newState.transferHistory.push(historyRecord);

                transferCount++;

                newState.playersForSale = newState.playersForSale.filter(id => id !== target.id);
                availableForSale = availableForSale.filter(p => p.id !== target.id);
            }
        }
    }

    return { newState, transferCount };
};

/**
 * AI teams release underperforming or excess players to free up wages
 * Players are released if they're old, low skill, or in poor form
 */
export const performAIReleases = (gameState: GameState): GameState => {
    const newState: GameState = JSON.parse(JSON.stringify(gameState));
    (newState as any).gameStarted = true;

    // Get AI teams (all teams except player team)
    const allTeams = Object.values(newState.leagues).flatMap((l: League) => l.teams);
    const aiTeams = allTeams.filter(t => t.name !== newState.playerTeamName);

    aiTeams.forEach(team => {
        // Find league for this team
        const teamLeagueName = Object.entries(newState.leagues).find(([_, league]) =>
            league.teams.some(t => t.name === team.name)
        )?.[0] || 'Premier League';

        // Identify candidates for release
        // Release if: too old (>34), very low skill (<40), terrible form (<-2), or poor fitness recovery
        const releaseCandidates = team.squad.filter(player => {
            // Don't release if in transfer cooldown
            if (isPlayerInTransferCooldown(player, newState.season, newState.currentWeek)) {
                return false;
            }

            // Scoring system for release likelihood
            let releaseScore = 0;

            // Age factor (much higher weight for older players)
            if (player.age > 34) releaseScore += 10;
            else if (player.age > 32) releaseScore += 5;

            // Skill factor (weak players)
            if (player.skill < 35) releaseScore += 10;
            else if (player.skill < 45) releaseScore += 5;

            // Form factor
            if (player.form < -2) releaseScore += 8;
            else if (player.form < 0) releaseScore += 3;

            // Fitness recovery (hard to maintain players)
            if (player.fitnessRecovery < 8) releaseScore += 6;

            // Keep at least 16 players in squad
            if (team.squad.length <= 18) releaseScore = 0;

            // Only release if score is significant
            return releaseScore >= 10;
        });

        // Release top candidates (max 1-2 per season)
        const toRelease = releaseCandidates
            .sort((a, b) => {
                // Sort by combination of age and skill
                const scoreA = (a.age - 25) * 2 + (50 - a.skill);
                const scoreB = (b.age - 25) * 2 + (50 - b.skill);
                return scoreB - scoreA;
            })
            .slice(0, Math.min(2, Math.max(0, team.squad.length - 18)));

        toRelease.forEach(player => {
            const releasePenalty = calculateReleasePenalty(player, teamLeagueName, newState.gameType, newState.config);

            // Only release if team can afford the penalty
            if (team.finances.balance >= releasePenalty) {
                // Remove from squad
                team.squad = team.squad.filter(p => p.id !== player.id);

                // Deduct penalty
                team.finances.balance -= releasePenalty;
                team.finances.history.push({
                    season: newState.season,
                    week: newState.currentWeek,
                    event: `Released ${player.name}`,
                    amount: -releasePenalty
                });

                // Mark when player was last transferred
                player.lastTransferredAt = { season: newState.season, week: newState.currentWeek };

                // Convert to free agent
                if (!newState.freeAgents) {
                    newState.freeAgents = [];
                }

                newState.freeAgents.push({
                    playerId: player.id,
                    playerName: player.name,
                    position: player.position,
                    skill: player.skill,
                    potential: player.potential,
                    age: player.age,
                    form: player.form,
                    fitness: player.fitness,
                    wage: player.wage,
                    hue: player.hue,
                    faceEmoji: player.faceEmoji,
                    playerSnapshot: JSON.parse(JSON.stringify(player)),
                    availableSince: { season: newState.season, week: newState.currentWeek },
                    totalBids: 0,
                    offers: [],
                    userBidStatus: 'no_bid',
                });

                // Record in transfer history
                newState.transferHistory.push({
                    season: newState.season,
                    week: newState.currentWeek,
                    playerName: player.name,
                    playerSkill: player.skill,
                    fromTeam: team.name,
                    toTeam: 'Free Agent',
                    fee: 0,
                    playerId: player.id,
                    playerAge: player.age,
                    playerPosition: player.position,
                    playerPotential: player.potential,
                });

                // Add news
                addNewsItem(
                    newState,
                    'Player Released',
                    `${player.name} has been released by ${team.name} and is now available as a free agent.`,
                    'transfer'
                );
            }
        });
    });

    return newState;
};